
import { useEffect, useState } from "react";
import Link from "@/lib/next-shims/router-link";
import { Modal, ModalHeader, ModalBody } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import {
  Bot,
  Apple,
  Monitor,
  Key,
  ExternalLink,
  Copy,
  Check,
} from "lucide-react";
import { JsonDisplay } from "@/components/ui/sql-display/JsonDisplay";

interface ConnectMcpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export type ClientTab = "claude-desktop" | "claude-code" | "cursor";
export type OsTab = "macos" | "windows";
export type TransportTab = "http" | "sse";

const BUILD_MCP_BASE_URL = import.meta.env.VITE_MCP_BASE_URL || "http://localhost:7100";
const API_KEY_ENV_NAME = "AGENT_BRAIN_API_KEY";
const API_KEY_PLACEHOLDER = "YOUR_API_KEY";

const API_KEY_VALUE_BY_CLIENT: Record<ClientTab, string> = {
  "claude-desktop": API_KEY_PLACEHOLDER,
  "claude-code": API_KEY_PLACEHOLDER,
  cursor: API_KEY_PLACEHOLDER,
};

const CLIENT_LABELS: Record<ClientTab, string> = {
  "claude-desktop": "Claude Desktop",
  "claude-code": "Claude Code",
  cursor: "Cursor",
};

function OsToggle({
  activeOs,
  onChange,
}: {
  activeOs: OsTab;
  onChange: (os: OsTab) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-700 dark:bg-gray-800">
      <button
        type="button"
        onClick={() => onChange("macos")}
        className={`flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
          activeOs === "macos"
            ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
            : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        }`}
        aria-label="Use macOS config path"
        aria-pressed={activeOs === "macos"}
        title="macOS"
      >
        <Apple className="size-3.5" />
        <span>macOS</span>
      </button>
      <button
        type="button"
        onClick={() => onChange("windows")}
        className={`flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
          activeOs === "windows"
            ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
            : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        }`}
        aria-label="Use Windows config path"
        aria-pressed={activeOs === "windows"}
        title="Windows"
      >
        <Monitor className="size-3.5" />
        <span>Windows</span>
      </button>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
      title="Copy"
    >
      {copied ? (
        <Check className="size-3.5 text-green-500" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </button>
  );
}

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

export function getServerUrl(baseUrl: string, transport: TransportTab): string {
  return `${normalizeBaseUrl(baseUrl)}/${transport === "http" ? "mcp" : "sse"}`;
}

export function getConfig(client: ClientTab, transport: TransportTab, serverUrl: string): string {
  const apiKeyValue = API_KEY_VALUE_BY_CLIENT[client];
  const serverConfig =
    client === "cursor"
      ? {
          url: serverUrl,
          headers: { "X-API-Key": apiKeyValue },
        }
      : {
          type: transport,
          url: serverUrl,
          headers: { "X-API-Key": apiKeyValue },
        };

  return JSON.stringify(
    {
      mcpServers: {
        "agent-brain": serverConfig,
      },
    },
    null,
    2
  );
}

export function getFilePath(client: ClientTab, os: OsTab): string {
  switch (client) {
    case "claude-desktop":
      return os === "macos"
        ? "~/Library/Application Support/Claude/claude_desktop_config.json"
        : "%APPDATA%\\Claude\\claude_desktop_config.json";
    case "claude-code":
      return ".mcp.json (project) or ~/.claude.json (user)";
    case "cursor":
      return os === "macos"
        ? "~/.cursor/mcp.json or .cursor/mcp.json"
        : "%USERPROFILE%\\.cursor\\mcp.json or .cursor\\mcp.json";
  }
}

function getCredentialHelp(client: ClientTab): string {
  switch (client) {
    case "claude-desktop":
      return `Replace ${API_KEY_PLACEHOLDER} with your API key. Use a local proxy if you do not want to store the key in Claude Desktop config.`;
    case "claude-code":
      return `Replace ${API_KEY_PLACEHOLDER} with your API key, or use \${${API_KEY_ENV_NAME}} if you prefer a shell environment variable.`;
    case "cursor":
      return `Replace ${API_KEY_PLACEHOLDER} with your API key, or use \${env:${API_KEY_ENV_NAME}} if you prefer a Cursor environment variable.`;
  }
}

export default function ConnectMcpModal({
  isOpen,
  onClose,
}: ConnectMcpModalProps) {
  const [client, setClient] = useState<ClientTab>("claude-desktop");
  const [os, setOs] = useState<OsTab>("macos");
  const [transport, setTransport] = useState<TransportTab>("http");
  const [mcpBaseUrl, setMcpBaseUrl] = useState(BUILD_MCP_BASE_URL);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    fetch("/api/runtime-config", { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload: { mcpBaseUrl?: string } | null) => {
        const runtimeBaseUrl = normalizeBaseUrl(payload?.mcpBaseUrl ?? "");
        if (!cancelled && runtimeBaseUrl) {
          setMcpBaseUrl(runtimeBaseUrl);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const serverUrl = getServerUrl(mcpBaseUrl, transport);
  const config = getConfig(client, transport, serverUrl);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" aria-label="Connect MCP">
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-linear-to-br from-brand-500 to-purple-600 text-white">
            <Bot className="size-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Connect MCP
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Use AI to manage connectors, workflows, and knowledge
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-5">
        {/* Step 1: API Key */}
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/40">
              <Key className="size-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                Step 1: Get your API Key
              </h4>
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                You need an API key to authenticate with the MCP server. Create
                one in{" "}
                <Link
                  href="/my-api-keys"
                  onClick={onClose}
                  className="inline-flex items-center gap-1 font-medium underline hover:no-underline"
                >
                  My API Keys
                  <ExternalLink className="size-3" />
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Step 2: Configure */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            Step 2: Add to your AI client
          </h4>

          <div className="mb-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-1.5">
                {(Object.keys(CLIENT_LABELS) as ClientTab[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setClient(c)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      client === c
                        ? "bg-primary text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    }`}
                  >
                    {CLIENT_LABELS[c]}
                  </button>
                ))}
              </div>
              {(client === "claude-desktop" || client === "cursor") && (
                <OsToggle activeOs={os} onChange={setOs} />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {(["http", "sse"] as TransportTab[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setTransport(mode)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    transport === mode
                      ? "bg-primary text-white shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  }`}
                >
                  {mode === "http" ? "Streamable HTTP" : "Legacy SSE"}
                </button>
              ))}
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 dark:border-gray-700 dark:bg-gray-900">
                <code className="min-w-0 flex-1 truncate font-mono text-xs text-gray-600 dark:text-gray-300">
                  {serverUrl}
                </code>
                <CopyButton text={serverUrl} />
              </div>
            </div>
          </div>

          {/* Config Block */}
          <div>
            <p className="mb-1.5 font-mono text-xs text-gray-500 dark:text-gray-400">
              {getFilePath(client, os)}
            </p>
            <JsonDisplay json={config} maxHeight="200px" />
          </div>

          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {getCredentialHelp(client)}
          </p>
        </div>

        {/* Step 3: Use */}
        <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 dark:border-brand-800 dark:bg-brand-900/20">
          <h4 className="mb-2 text-sm font-semibold text-brand-700 dark:text-brand-300">
            Step 3: Start using
          </h4>
          <ol className="space-y-1.5 text-xs text-brand-600 dark:text-brand-400">
            <li className="flex gap-2">
              <span className="font-bold">1.</span>
              Open {CLIENT_LABELS[client]} or any MCP-compatible AI client
            </li>
            <li className="flex gap-2">
              <span className="font-bold">2.</span>
              Describe what you want in natural language
            </li>
            <li className="flex gap-2">
              <span className="font-bold">3.</span>
              AI will auto-discover schemas, create workflows, manage knowledge
            </li>
          </ol>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Link href="/my-api-keys" onClick={onClose}>
            <Button>
              <Key className="mr-2 size-4" />
              Go to API Keys
            </Button>
          </Link>
        </div>
      </ModalBody>
    </Modal>
  );
}
